/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import {Autocomplete, Box, Button, Paper, Stack, TextField, Typography} from '@mui/material';
import {type ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import type {Component, CustomField, Issue, Project} from '@/types/atlassian';

type Props = {
    accessToken: string;
    project: Project;
    webhookId: string;
    jiraInstanceId: string;
    batchSize: number;
    notBefore: string;
    sendIssuesToFM: (payload: string) => void;
    sentLastBatch: (payload: string) => void;
};

export type IssueSearchResult = {
    isLast: boolean;
    issues: Issue[];
    names: unknown;
    nextPageToken: string | null;
    schema: unknown;
};

const fetchIssues = async (
    accessToken: string,
    nextPageToken: string | null,
    jql: string,
    jiraInstanceId: string,
    extraFields: string[],
): Promise<IssueSearchResult> => {
    const result = await fetch(
        `https://api.atlassian.com/ex/jira/${jiraInstanceId}/rest/api/3/search/jql`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                jql,
                fields: [
                    'project',
                    'components',
                    'fixVersions',
                    'issuetype',
                    'summary',
                    'timeoriginalestimate',
                    'resolution',
                    'status',
                    'created',
                    ...extraFields,
                ],
                fieldsByKeys: false,
                maxResults: 200,
                nextPageToken: nextPageToken,
            }),
        },
    );
    return (await result.json()) as IssueSearchResult;
};

type ComponentSelect = Component & {
    label: string;
};

const SyncBox = (props: {children: ReactElement}) => {
    return (
        <Paper
            sx={{
                width: 1,
            }}
            variant='outlined'
        >
            <Box sx={{p: 2}}>{props.children}</Box>
        </Paper>
    );
};

export const SyncProject = ({
    accessToken,
    project,
    webhookId,
    sendIssuesToFM,
    jiraInstanceId,
    batchSize,
    sentLastBatch,
    notBefore,
}: Props): JSX.Element => {
    const [status, setStatus] = useState<string>('');
    const [running, setRunning] = useState(false);
    const [components, setComponents] = useState<ComponentSelect[]>([]);
    const [selectedComponent, setSelectedComponent] = useState<ComponentSelect | null>(null);

    const notBeforeDate = useMemo(() => {
        if (notBefore && notBefore.length > 0) {
            const beforeDate = new Date(notBefore);

            if (!isNaN(beforeDate.valueOf())) {
                return beforeDate;
            }
        }

        return null;
    }, [notBefore]);

    const fetchComponents = async () => {
        const result = await fetch(
            `https://api.atlassian.com/ex/jira/${jiraInstanceId}/rest/api/3/project/${project.key}/components`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return (await result.json()) as Component[];
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: use effect
    useEffect(() => {
        const run = async () => {
            const components = await fetchComponents();
            const selectComponents = components.map(c => ({...c, label: c.name}));
            setComponents(selectComponents);

            if (selectComponents.length) {
                const pm = selectComponents.find(c => c.name === 'Project Management');
                setSelectedComponent(pm ?? selectComponents[1]);
            }
        };

        run().catch(() => {
            setStatus('error fetching components');
        });
    }, []);

    const fetchCustomFields = async (): Promise<CustomField[]> => {
        const result = await fetch(
            `https://api.atlassian.com/ex/jira/${jiraInstanceId}/rest/api/2/field`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );
        return (await result.json()) as CustomField[];
    };

    const syncIssues = useCallback(
        (jql: string, ignoreNotBefore: boolean) => {
            const run = async () => {
                setRunning(true);
                setStatus('Starting sync');
                const fields = await fetchCustomFields();
                const epicName = fields.find(f => f.name === 'Epic Name')?.key;
                const epicLink = fields.find(f => f.name === 'Epic Link')?.key;
                const storyPoints = fields.find(f => f.name === 'Story Points')?.key;
                const dueDate = fields.find(
                    f => f.name === 'Due Date' && f.key.startsWith('customfield_'),
                )?.key;
                const customFields: string[] = [epicName, epicLink, storyPoints, dueDate].filter(
                    Boolean,
                ) as string[];
                // eslint-disable-next-line prefer-const
                let {issues, nextPageToken} = await fetchIssues(
                    accessToken,
                    null,
                    jql,
                    jiraInstanceId,
                    customFields,
                );
                setStatus(`Starting sync of issues`);

                do {
                    const result = await fetchIssues(
                        accessToken,
                        nextPageToken,
                        jql,
                        jiraInstanceId,
                        customFields,
                    );
                    issues = [...issues, ...result.issues];
                    nextPageToken = result.nextPageToken;
                    setStatus(`Fetched ${issues.length} issues`);
                } while (nextPageToken != null);

                issues = issues.sort((a, b) => {
                    if (a.fields.issuetype.name === 'Epic') {
                        return -1;
                    }

                    if (b.fields.issuetype.name === 'Epic') {
                        return 1;
                    }

                    return a.fields.created.localeCompare(b.fields.created);
                });

                let mappedIssues = issues.map(issue => {
                    const issueDueDate =
                        issue.fields.duedate ??
                        (dueDate &&
                        dueDate in issue.fields &&
                        typeof issue.fields[dueDate] === 'string'
                            ? (issue.fields[dueDate] as string)
                            : null);

                    const issueEpicLink =
                        epicLink &&
                        epicLink in issue.fields &&
                        typeof issue.fields[epicLink] === 'string'
                            ? (issue.fields[epicLink] as string)
                            : null;

                    const issueEpicName =
                        epicName &&
                        epicName in issue.fields &&
                        typeof issue.fields[epicName] === 'string'
                            ? (issue.fields[epicName] as string)
                            : null;

                    const issueStoryPoints =
                        storyPoints &&
                        storyPoints in issue.fields &&
                        (typeof issue.fields[storyPoints] === 'number' ||
                            typeof issue.fields[storyPoints] === 'string')
                            ? (issue.fields[storyPoints] as number | string)
                            : null;

                    return {
                        webhookId: webhookId,
                        webhookEvent: 'jira:issue_updated',
                        timestamp: new Date().toISOString(),
                        id: issue.id,
                        key: issue.key,
                        summary: issue.fields.summary,
                        timeoriginalestimate: issue.fields.timeoriginalestimate,
                        status: {
                            id: issue.fields.status.id,
                            name: issue.fields.status.name,
                        },
                        project: {
                            id: issue.fields.project.id,
                            key: issue.fields.project.key,
                        },
                        component: {
                            id: issue.fields.components[0]?.id ?? '-1',
                            name: issue.fields.components[0]?.name ?? 'No Component',
                        },
                        resolution: {
                            id: issue.fields.resolution?.id,
                            name: issue.fields.resolution?.name,
                        },
                        issuetype: {
                            id: issue.fields.issuetype.id,
                            name: issue.fields.issuetype.name,
                        },
                        version: issue.fields.fixVersions.length
                            ? {
                                  id: issue.fields.fixVersions[0].id,
                                  description: issue.fields.fixVersions[0].description,
                                  name: issue.fields.fixVersions[0].name,
                                  archived: issue.fields.fixVersions[0].archived,
                                  released: issue.fields.fixVersions[0].released,
                                  releaseDate: issue.fields.fixVersions[0].releaseDate,
                              }
                            : {},
                        dueDate: issueDueDate,
                        storyPoints: issueStoryPoints,
                        epicLink: issueEpicLink,
                        epicName: issueEpicName,
                        created: issue.fields.created,
                    };
                });

                if (notBeforeDate && !ignoreNotBefore) {
                    mappedIssues = mappedIssues.filter(i => new Date(i.created) >= notBeforeDate);
                }

                //{ payload, batchNumber, batchTotalCount, totalItemCount, correlationId }
                const totalItemCount = mappedIssues.length;
                const batchTotalCount = Math.ceil(mappedIssues.length / batchSize);
                const correlationId = crypto.randomUUID();
                let batchNumber = 1;

                while (mappedIssues.length > 0) {
                    sendIssuesToFM(
                        JSON.stringify({
                            payload: mappedIssues.splice(0, batchSize),
                            batchNumber: batchNumber++,
                            batchTotalCount,
                            totalItemCount,
                            correlationId,
                        }),
                    );
                    await new Promise(resolve => setTimeout(resolve, 100));
                }

                sentLastBatch(
                    JSON.stringify({
                        correlationId,
                    }),
                );
                setStatus('Issues sent to FileMaker');
                setRunning(false);
            };

            run().catch(e => {
                setStatus('Error Syncing issues');
                console.error('syncIssues', e);
                setRunning(false);
            });
        },
        [accessToken],
    );

    return (
        <>
            <Typography>JIRA Project: {project.name}</Typography>
            <Stack spacing={2} direction='column'>
                <Typography>{status}</Typography>
                <SyncBox>
                    <Button
                        variant='contained'
                        onClick={() => {
                            syncIssues(`project = "${project.key}"`, false);
                        }}
                        disabled={running}
                    >
                        Sync All Issues
                    </Button>
                </SyncBox>
                <SyncBox>
                    <>
                        <Typography>Sync issues for one component</Typography>
                        <Autocomplete
                            disablePortal
                            id='combo-box-demo'
                            options={components}
                            sx={{width: 300, pt: 3}}
                            value={selectedComponent}
                            onChange={(_event, value) => {
                                const component = components.find(p => p.id === value?.id) ?? null;
                                setSelectedComponent(component);
                            }}
                            renderInput={params => <TextField {...params} label='Component' />}
                        />
                        <Button
                            variant='contained'
                            sx={{mt: 2}}
                            onClick={() => {
                                if (!selectedComponent) {
                                    return;
                                }

                                const componentName = selectedComponent.name
                                    .replaceAll('"', '\\"')
                                    .replaceAll('\\', '\\\\');
                                syncIssues(
                                    `project = "${project.key}"  and component = "${componentName}"`,
                                    false,
                                );
                            }}
                            disabled={!selectedComponent || running}
                        >
                            Sync Issues for component
                        </Button>
                    </>
                </SyncBox>
                <SyncBox>
                    <>
                        <Typography>Sync epics</Typography>
                        <Button
                            variant='contained'
                            sx={{mt: 2}}
                            onClick={() => {
                                syncIssues(`project = "${project.key}" and issuetype = Epic`, true);
                            }}
                            disabled={running}
                        >
                            Sync All Epics for project
                        </Button>
                    </>
                </SyncBox>
            </Stack>
        </>
    );
};
